import {trans} from '@frontend/helpers'
import {ChatsApi} from '@frontend/modules/private/chats/ChatsApi'
import type {ChatMessageData} from '@frontend/modules/private/chats/ChatsModuleTypes'
import {
    MyExecutorsApi,
    type MyPublishedExecutorsAsOptionsResponseData,
} from '@frontend/modules/private/executors/MyExecutorsApi'
import type {AnyObject} from '@frontend/types/AppTypes'
import {useAutogrowOnTextArea} from '@shared/helpers/useAutogrowOnTextArea'
import type {CategoryType} from '@shared/types/Categories'
import {
    type ChangeEvent,
    useState,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Input} from 'swayok-react-mdb-ui-kit/components/Input/Input'
import {SelectInput} from 'swayok-react-mdb-ui-kit/components/Input/SelectInput/SelectInput'
import {Modal} from 'swayok-react-mdb-ui-kit/components/Modal/Modal'
import {ModalBody} from 'swayok-react-mdb-ui-kit/components/Modal/ModalBody'
import {ModalContent} from 'swayok-react-mdb-ui-kit/components/Modal/ModalContent'
import {ModalFooter} from 'swayok-react-mdb-ui-kit/components/Modal/ModalFooter'
import {ModalFooterCloseButton} from 'swayok-react-mdb-ui-kit/components/Modal/ModalFooterCloseButton'
import {ModalHeader} from 'swayok-react-mdb-ui-kit/components/Modal/ModalHeader'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {ToastService} from 'swayok-react-mdb-ui-kit/services/ToastService'
import {AnalyticsEventReporter} from '../../../../../helpers/AnalyticsEventReporter'
import type {
    AdDetailsData,
    AdPreviewDataForList,
} from '../../AdsModuleTypes'

interface AdDetailsSendMessageToSellerModalProps {
    show: boolean | null
    depth?: number
    ad: AdDetailsData | AdPreviewDataForList
    categoryName: string
    categoryType: CategoryType
    onClose: () => void
    onClosed?: () => void
    onMessageSent: (chatId: number) => void
}

// Модальное окно отправки сообщения продавцу.
export function AdDetailsSendMessageToSellerModal(props: AdDetailsSendMessageToSellerModalProps) {

    const {
        show,
        depth,
        ad,
        categoryName,
        categoryType,
        onClose,
        onClosed,
        onMessageSent,
    } = props

    const [
        executors,
        setExecutors,
    ] = useState<MyPublishedExecutorsAsOptionsResponseData['options'] | null>(null)

    // Сообщение.
    const [
        messageText,
        setMessageText,
    ] = useState<string>('')

    // Профиль исполнителя услуг.
    const [
        executorId,
        setExecutorId,
    ] = useState<number | null>(null)

    // Состояние отправки сообщения.
    const [
        isProcessing,
        setIsProcessing,
    ] = useState<boolean>(false)

    // Работа с авто-высотой текстового поля ввода.
    const {
        inputRefHandler,
        inputElement,
    } = useAutogrowOnTextArea({
        value: messageText,
    })

    // Загрузить список исполнителей услуг авторизованного пользователя
    // для объявлений в категории "Нужна услуга".
    const loadExecutors = () => {
        if (categoryType === 'service_requests' && executors === null) {
            MyExecutorsApi.getMyPublishedExecutorsAsOptions()
                .then(options => {
                    setExecutors(options)
                    console.log(categoryName, options, options.find(
                        executor => executor.extra?.categoryName === categoryName
                    ))
                    setExecutorId(
                        options.find(
                            executor => executor.extra?.categoryName === categoryName
                        )?.value ?? null
                    )
                })
                .catch(() => {
                    // Игнорируем.
                })
        }
    }

    // Обработка события начала открытия модального окна.
    const onShow = useEventCallback((): void => {
        setTimeout(() => {
            inputElement?.focus()
        }, 200)
        loadExecutors()
    })

    // Отправить сообщение.
    const sendMessage = useEventCallback((): void => {
        if (isProcessing || messageText.trim() === '') {
            return
        }
        setIsProcessing(true)
        ChatsApi.addMessageToChat(messageText.trim(), null, ad.id, executorId)
            .then((messageData: ChatMessageData): void => {
                ToastService.success(trans().ads.messages.chat_message_sent)
                onMessageSent(messageData.chatId)
                setMessageText('')
                AnalyticsEventReporter.startChatWithSeller(ad)
            })
            .catch((error: ApiError) => {
                handleErrorResponse(error, {
                    suppressValidationErrorMessage: true,
                    validationErrors(errors: AnyObject<string>): void {
                        const messages: string[] = Object.values(errors)
                        ToastService.error(
                            messages.join('; ').replace(/\.;/g, ';'),
                            messages.length > 2 ? 8000 : 5000
                        )
                    },
                })
            })
            .finally(() => {
                setIsProcessing(false)
            })
    })

    return (
        <Modal
            show={show}
            depth={depth}
            onClose={onClose}
            onClosed={onClosed}
            onShow={onShow}
            size="md"
            staticBackdrop
            closeOnEsc
        >
            <ModalContent>
                <ModalHeader
                    title={trans().ads.details.send_message_modal.header}
                    onClose={onClose}
                />
                <ModalBody>
                    <form
                        className="m-0 flex-1"
                        onSubmit={event => {
                            event.preventDefault()
                            sendMessage()
                        }}
                    >
                        <Input
                            textarea
                            label={trans().ads.details.send_message_modal.message}
                            value={messageText}
                            wrapperClassName="m-0"
                            className="autogrow"
                            style={{
                                minHeight: 110,
                                maxHeight: 290,
                            }}
                            disabled={isProcessing}
                            inputRef={inputRefHandler}
                            onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                                setMessageText(event.currentTarget.value)
                            }}
                            onBlur={() => {
                                setMessageText(messageText.trim())
                            }}
                        />
                        {categoryType === 'service_requests' && executors?.length && (
                            <SelectInput<number | null>
                                label={trans().ads.details.send_message_modal.executor}
                                options={executors}
                                value={executorId}
                                wrapperClassName="mt-4"
                                withEmptyOption={{
                                    label: trans().ads.details.send_message_modal.without_executor,
                                    value: null,
                                    attributes: {
                                        className: 'text-muted',
                                    },
                                }}
                                onChange={value => {
                                    setExecutorId(value)
                                }}
                            />
                        )}
                    </form>
                </ModalBody>
                <ModalFooter flexBetween>
                    <ModalFooterCloseButton
                        title={trans().common.modal.cancel}
                        className="d-none d-sm-block"
                        onClose={onClose}
                    />
                    <Button
                        color="green"
                        className="full-width-sm-down"
                        disabled={isProcessing || messageText.trim() === ''}
                        onClick={sendMessage}
                    >
                        {trans().ads.details.send_message_modal.submit}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}
